/* Tech Notes 
  Parent owns the source of truth
  Child keeps its own internal working copy
  Child only commits to parent when the row is valid..This means the child needs a temporary working state.
  parent must use UseMem.
*/
import { GoabButton, GoabButtonGroup, GoabCallout, GoabIcon, GoabIconButton, GoabModal, GoabRadioGroup, GoabRadioItem, GoabTable } from "@abgov/react-components";
import { GoabRadioGroupOnChangeDetail } from "@abgov/ui-components-common/lib/common";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";

import "./TravelInfo.scss"
import { TravelClaimsExpenses } from "../../../../../common/types";
import { wdpUtil } from "../../../../../common/utils/util";

export interface TravelProps {
  updateFlow: boolean;
  canShowEdit?: boolean;
  values: TravelClaimsExpenses[];
  referenceDate?: string | null;
  activeSection?: string | null;
  isBusy?: boolean;
  onChange: (changeType:string,change:TravelClaimsExpenses|TravelClaimsExpenses[]|boolean|null ) => void;
}




export function TravelInfo(props: TravelProps) {

  type DeleteModalType = 'delete-with-remaining' | 'delete-last-row';

  const [secStatus, setSecStatus]   = useState({editMode: !props.updateFlow});
  const [deleteModalState, setDeleteModalState] = useState<{ type: DeleteModalType } | null>(null);
  // form Control state managment
  type TravelRowState = {values: TravelClaimsExpenses;errors: TravelClaimsExpenses;touched: TravelClaimsExpenses};
  const emptyState: TravelClaimsExpenses = {id: "",from: "",to: "",startUtc: "",finishUtc: "",noOfUnits: ""};
  const loadedTouchedState: TravelClaimsExpenses = {id: "",from: "T",to: "T",startUtc: "T",finishUtc: "T",noOfUnits: ""};
  
  // INTERNAL STATE INITIALIZE
  const [formCtrl, setFormCtrl] = useState<TravelRowState[]>(() => {
    if (props.values.length === 0) {
      // No rows from parent → create one new row
      return [{values: {id: wdpUtil.getUniqueId(),from: "",to: "",startUtc: "",finishUtc: "",noOfUnits: 0},errors: { ...emptyState },touched: { ...emptyState }}];
    }
    // Parent sent rows → initialize them
    return props.values.map(v => ({values: v,errors: { ...emptyState },touched: { ...loadedTouchedState }}));
  });

  const [eligibility, setEligibility] = useState<{original: boolean; local: boolean}>({
    original: props.values.length > 0,
    local:    formCtrl.length > 0
  });
  const showTravelDetails = eligibility.local === true;
  
  /***************************************************************************************
   * . SYNC INTERNAL STATE WHEN PROPS CHANGE
  ****************************************************************************************/
  useEffect(() => {
    setFormCtrl(prev => {
    const prevMap = new Map(prev.map(r => [r.values.id, r]));

    return props.values.map(v => {
      const existing = prevMap.get(v.id);
      if (existing) {
        return {values: v,errors: { ...emptyState },touched: { ...loadedTouchedState }};
      }
      return {values: v,errors: { ...emptyState },touched: { ...loadedTouchedState }};
      });
    });
  }, [props.values]);
  
  useEffect(() => {
    const val = props.values.length > 0;
    setEligibility({ original: val, local: val });
  }, [props.values, props.updateFlow]);

  useEffect(() => {
    setSecStatus({ editMode: !props.updateFlow });
  }, [props.updateFlow]);

  // Enforce single-active-section restriction: if another section becomes the active
  // one being edited, auto-cancel this section's own in-progress edit.
  useEffect(() => {
    if (props.activeSection !== undefined && props.activeSection !== 'travel' && secStatus.editMode && props.updateFlow) {
      onCancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.activeSection]);

  //*********************************************************************************
  // Check if section is dirty (different from original props) 
  //*********************************************************************************/
  function isDirty(): boolean {
    
    // 1 - Check if travel rows changed (count )
    if (formCtrl.length !== props.values.length) {
      // If more rows added, the last added row must be valid
      if (formCtrl.length > props.values.length) {
        return isRowValid(formCtrl.length - 1);
      }
      // If fewer rows (deleted), that's a change
      return true;
    }

    // Same number of rows - check if content changed
    return formCtrl.some((rowState, index) => {
      const originalRow = props.values[index];
      if (!originalRow) return true;
      return (
        rowState.values.from      !== originalRow.from      ||
        rowState.values.to        !== originalRow.to        ||
        rowState.values.startUtc  !== originalRow.startUtc  ||
        rowState.values.finishUtc !== originalRow.finishUtc
      );
    });
  }
  const isSectionDirty = isDirty();
  const isSaveEnabled = isSectionDirty;
  //#######################################################
  function getReferenceDate(): Dayjs {
    const parsed = dayjs(props.referenceDate);
    return parsed.isValid() ? parsed : dayjs();
  }

  function parseTimeValue(value: string): Dayjs | null {
    const trimmed = String(value || "").trim();
    if (!trimmed) return null;

    const asIso = dayjs(trimmed);
    if (asIso.isValid()) return asIso;

    const hhmm = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(trimmed);
    if (!hhmm) return null;

    const ref = getReferenceDate();
    return ref.hour(Number(hhmm[1])).minute(Number(hhmm[2])).second(0).millisecond(0);
  }

  function toReferenceDateTime(value: string, isFinishTime?: boolean, startValue?: string): Dayjs | null {
    const parsed = parseTimeValue(value);
    if (!parsed || !parsed.isValid()) return null;
    const ref = getReferenceDate();
    let anchored = ref.hour(parsed.hour()).minute(parsed.minute()).second(0).millisecond(0);
    
    // Detect overnight travel: if this is finish time and it's strictly earlier than start time, add 1 day.
    // NOTE: equal times must NOT be bumped to next day, otherwise a 09:00 -> 09:00 entry would
    // incorrectly calculate as a full 24-hour total instead of being flagged as an invalid (0 hour) entry.
    if (isFinishTime && startValue) {
      const startParsed = parseTimeValue(startValue);
      if (startParsed && startParsed.isValid()) {
        const startAnchored = ref.hour(startParsed.hour()).minute(startParsed.minute()).second(0).millisecond(0);
        // If finish time is before start time (on clock), assume next day
        if (anchored.isBefore(startAnchored)) {
          anchored = anchored.add(1, 'day');
        }
      }
    }
    
    return anchored;
  }

  function recalcNoOfUnits(row: TravelRowState) {
    const start = toReferenceDateTime(String(row.values.startUtc || ""));
    const finish = toReferenceDateTime(String(row.values.finishUtc || ""), true, String(row.values.startUtc || ""));
    if (!start || !finish) {
      row.values.noOfUnits = 0;
      return;
    }
    const diff = finish.diff(start, "hour", true);
    row.values.noOfUnits = diff > 0 ? Number(diff.toFixed(2)) : 0;
  }

  function formatTimeOnly(value: string | number | null | undefined): string {
    if (value === null || value === undefined || String(value).trim() === "") {
      return "";
    }

    const parsed = parseTimeValue(String(value));
    return parsed ? parsed.format("HH:mm") : String(value);
  }

  // Displays decimal hours (e.g. 3.33) as a clock duration "H:MM" (e.g. "3:20"),
  // matching the 24h HH:mm format used for the start/stop time fields.
  function formatHoursAsDuration(value: string | number | null | undefined): string {
    const hours = typeof value === "number" ? value : parseFloat(String(value ?? ""));
    if (!Number.isFinite(hours) || hours <= 0) {
      return "";
    }
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}:${String(m).padStart(2, "0")}`;
  }

  /***************************************************************************************
  // Eligibility changed
  ****************************************************************************************/
  function handleEligibility(detail: GoabRadioGroupOnChangeDetail) {
    
    const newEligibility = detail.value === "1";
    setEligibility(prev => ({ ...prev, local: newEligibility }));

    if (!newEligibility) {
      // Clear all rows when selecting NO
      const emptyState: TravelClaimsExpenses = {id: "",from: "",to: "",startUtc: "",finishUtc: "",noOfUnits: ""};
      setFormCtrl([{values: {id: wdpUtil.getUniqueId(),from: "",to: "",startUtc: "",finishUtc: "",noOfUnits: 0},errors: { ...emptyState },touched: { ...emptyState }}]);
    }
  }
  /***************************************************************************************
  // OnAdd New Row
  ****************************************************************************************/
  function onAddRow() {
    
    const newRow: TravelClaimsExpenses = {id: wdpUtil.getUniqueId(),from: "",to: "",startUtc: "",finishUtc: "",noOfUnits: 0};
    const emptyState: TravelClaimsExpenses = {id: "",from: "",to: "",startUtc: "",finishUtc: "",noOfUnits: ""};
    setFormCtrl(prev => [...prev,{
        values: newRow,
        errors: { ...emptyState },
        touched: { ...emptyState }
      }
    ]);
    // Parent is NOT notified yet — row is empty/invalid
  }
  /***************************************************************************************
  // OnDel Row
  ****************************************************************************************/
  function onDeleteRow(index: number) {
    doDeleteRow(index);
  }

  function doDeleteRow(index: number) {
    setFormCtrl(prev => {
      const updated = prev.filter((_, i) => i !== index);
      if (eligibility.local === true && updated.length === 0) {
        // Auto-revert to "No" since no valid entries exist
        setEligibility(prev => ({ ...prev, local: false }));
      }
      return updated;
    });
  }
  /***************************************************************************************
  // OnEdit mode
  ****************************************************************************************/
  function onEdit(){
    props.onChange("editStart", true);
    setSecStatus({editMode:!secStatus.editMode});
  }
  /***************************************************************************************
  // OnCancel
  ****************************************************************************************/
  function onCancel(){
    const val = props.values.length > 0;
    setEligibility({ original: val, local: val });
    
    // Restore travel rows to their original state from props
    const emptyState: TravelClaimsExpenses = {id: "",from: "",to: "",startUtc: "",finishUtc: "",noOfUnits: ""};
    const loadedTouchedState: TravelClaimsExpenses = {id: "",from: "T",to: "T",startUtc: "T",finishUtc: "T",noOfUnits: ""};
    
    setFormCtrl(props.values.length === 0 
      ? [{values: {id: wdpUtil.getUniqueId(),from: "",to: "",startUtc: "",finishUtc: "",noOfUnits: 0},errors: { ...emptyState },touched: { ...emptyState }}]
      : props.values.map(v => ({values: v,errors: { ...emptyState },touched: { ...loadedTouchedState }}))
    );
    
    setSecStatus({editMode: !props.updateFlow});
  }

  function restoreFromProps() {
    const val = props.values.length > 0;
    setEligibility({ original: val, local: val });

    const emptyState: TravelClaimsExpenses = {id: "",from: "",to: "",startUtc: "",finishUtc: "",noOfUnits: ""};
    const loadedTouchedState: TravelClaimsExpenses = {id: "",from: "T",to: "T",startUtc: "T",finishUtc: "T",noOfUnits: ""};

    setFormCtrl(props.values.length === 0
      ? [{values: {id: wdpUtil.getUniqueId(),from: "",to: "",startUtc: "",finishUtc: "",noOfUnits: 0},errors: { ...emptyState },touched: { ...emptyState }}]
      : props.values.map(v => ({values: v,errors: { ...emptyState },touched: { ...loadedTouchedState }}))
    );
  }

  /***************************************************************************************
  // OnSave - confirmation gate only
  ****************************************************************************************/
  function onSave(){
    const originalIds = new Set(props.values.map(row => String(row.id || "")));
    const currentDraftIds = new Set(formCtrl.map(row => String(row.values.id || "")));
    const hasDeletionImpact = Array.from(originalIds).some(id => !currentDraftIds.has(id));

    if (hasDeletionImpact) {
      const modalType: DeleteModalType = formCtrl.length > 0 ? 'delete-with-remaining' : 'delete-last-row';
      setDeleteModalState({ type: modalType });
      return;
    }

    doSave();
  }

  /***************************************************************************************
  // doSave - validation and execute save
  ****************************************************************************************/
  function doSave(){
    const fieldsToValidate: (keyof TravelClaimsExpenses)[] = ["from", "to", "startUtc", "finishUtc"];
    const updatedRows = formCtrl.map(rowState => {
      const row = {...rowState,errors: { ...rowState.errors },touched: { ...rowState.touched }};
      for (const field of fieldsToValidate) {
        row.touched[field] = "T";
        row.errors[field] = validateField(field, row.values[field], row.values) as any;
      }
      const rowHasErrors = row.errors.from !== "" || row.errors.to !== "" || row.errors.startUtc !== "" || row.errors.finishUtc !== "";
      if (!rowHasErrors) {
        recalcNoOfUnits(row);
        // Validate hours after calculation
        row.errors.noOfUnits = validateField("noOfUnits", row.values.noOfUnits, row.values) as any;
      }
      return row;
    });

    setFormCtrl(updatedRows);
    const hasErrors = updatedRows.some(row => row.errors.from !== "" || row.errors.to !== "" || row.errors.startUtc !== "" || row.errors.finishUtc !== "" || row.errors.noOfUnits !== "");
    if (hasErrors) {
      return;
    }
    const validRows = updatedRows
      .filter(row =>row.errors.from === "" && row.touched.from === "T" && row.errors.to === "" && row.touched.to === "T" && row.errors.startUtc === "" && row.touched.startUtc === "T" && row.errors.finishUtc === "" && row.touched.finishUtc === "T" && row.errors.noOfUnits === "")
      .map(row => {
        const normalizedStart = toReferenceDateTime(String(row.values.startUtc || ""));
        const normalizedFinish = toReferenceDateTime(String(row.values.finishUtc || ""), true, String(row.values.startUtc || ""));
        return {
          ...row.values,
          startUtc: normalizedStart ? normalizedStart.toISOString() : String(row.values.startUtc || ""),
          finishUtc: normalizedFinish ? normalizedFinish.toISOString() : String(row.values.finishUtc || "")
        };
      });

    
    // ready to inform the parent that the rows are valid and can be saved
    props.onChange("save", validRows);

    // In create flow there is no Edit button, so keep section editable after save.
    setSecStatus({editMode: !props.updateFlow});
  }

  function onCancelDeleteModal() {
    setDeleteModalState(null);
    restoreFromProps();
  }

  function onConfirmDeleteModal() {
    if (deleteModalState !== null) {
      setDeleteModalState(null);
      doSave();
    }
  }
  /***************************************************************************************
  // On value changed
  ****************************************************************************************/
  function onMyChange(who: keyof TravelClaimsExpenses, val: string | null, index: number) {
    setFormCtrl(prev => {
      const updated = [...prev];
      const row = { ...updated[index] };
      const newValue = typeof val === "string" ? val : "";
      row.values  = { ...row.values,  [who]: newValue };
      row.touched = { ...row.touched, [who]: "T" };
      row.errors  = { ...row.errors,  [who]: validateField(who, newValue, row.values) };
      updated[index] = row;
      return updated;
    });
  }
  /***************************************************************************************
  // On field blur
  ****************************************************************************************/
  function onMyBlur(index: number, who: keyof TravelClaimsExpenses) {
    
    setFormCtrl(prev => {
      const updated = [...prev];
      const row     = { ...updated[index] };
      
      // 1. Mark field as touched
      row.touched = {...row.touched,[who]: "T"};
      
      // 2. Validate the field
      const fldErr= validateField(who, row.values[who], row.values);
      row.errors  = {...row.errors,[who]: fldErr};

      //
      // 3. If this is a time field, revalidate both start & finish
      //
      if (who === "startUtc" || who === "finishUtc") {
        const startErr = validateField("startUtc", row.values.startUtc, row.values);
        const finishErr = validateField("finishUtc", row.values.finishUtc, row.values);

        row.errors.startUtc = startErr;
        row.errors.finishUtc = finishErr;

        // Recalculate hours only if both times are valid
        if (startErr === "" && finishErr === "") {
          recalcNoOfUnits(row);
        } else {
          row.values.noOfUnits = 0;
        }
      }

      updated[index] = row;
      return updated;
    });
  }
  /***************************************************************************************
  ****************************************************************************************/
  function validateField(who: keyof TravelClaimsExpenses,value: any,row: TravelClaimsExpenses): string {

    // 1. Required text fields - Location From
    if (who === "from" && !String(value).trim()) {
      return "Enter travel origin.";
    }

    // 2. Required text fields - Location To
    if (who === "to" && !String(value).trim()) {
      return "Enter travel destination.";
    }

    // 3. Time fields validation
    if (who === "startUtc" || who === "finishUtc") {
      // 3a. Required check
      if (!String(value || "").trim()) {
        return who === "startUtc"
          ? "Select a start time."
          : "Select a stop time.";
      }

      // 3b. Invalid time format
      const isFinish = who === "finishUtc";
      const startVal = isFinish ? String(row.startUtc || "") : undefined;
      const val = toReferenceDateTime(String(value), isFinish, startVal);

      if (!val) {
        return "Invalid time format.";
      }

      // 3c. Stop time must be later than start time (only validate if both times exist)
      if (who === "finishUtc" && row.startUtc) {
        const startTime = toReferenceDateTime(String(row.startUtc), false, undefined);
        const finishTime = toReferenceDateTime(String(value), true, String(row.startUtc));
        
        if (startTime && finishTime && !finishTime.isAfter(startTime)) {
          return "Stop time must be later than start time.";
        }
      }
    }

    // 4. Total hours validation (noOfUnits)
    if (who === "noOfUnits") {
      const hours = typeof value === "number" ? value : parseFloat(String(value || "0"));
      if (hours <= 0) {
        return "Total hours must be greater than 0.";
      }
      if (hours > 24) {
        return "Total hours cannot exceed 24 hours.";
      }
    }

    return "";
  }

  /***************************************************************************************
  ****************************************************************************************/
  function isRowValid(index: number): boolean {
    const row = formCtrl[index];
    if (!row) return false;

    return (
      row.errors.from === ""      && row.touched.from === "T"     &&
      row.errors.to === ""        && row.touched.to === "T"       &&
      row.errors.startUtc === ""  && row.touched.startUtc === "T" &&
      row.errors.finishUtc === "" && row.touched.finishUtc === "T" 
    );
  }

  function onTimeFieldBlur(index: number, who: "startUtc" | "finishUtc", rawValue: string) {
    const committed = wdpUtil.normalizeTypedTimeInput(rawValue);
    setFormCtrl(prev => {
      const updated = [...prev];
      const row = { ...updated[index] };

      // Store committed value
      row.values = { ...row.values, [who]: committed };
      row.touched = { ...row.touched, [who]: "T" };

      // Revalidate both time fields so overnight works correctly
      const startErr  = validateField("startUtc", row.values.startUtc,  row.values);
      const finishErr = validateField("finishUtc", row.values.finishUtc, row.values);
      row.errors = { ...row.errors, startUtc: startErr, finishUtc: finishErr };

      if (startErr === "" && finishErr === "") {
        recalcNoOfUnits(row);
      } else {
        row.values.noOfUnits = 0;
      }

      updated[index] = row;
      return updated;
    });
  }

  function renderTimeField(index: number, rowState: TravelRowState, who: "startUtc" | "finishUtc") {
    if (!secStatus.editMode) {
      return <span>{formatTimeOnly(rowState.values[who] as string)}</span>;
    }

    const isStart = who === "startUtc";
    const fieldValue = rowState.values[who] as string;
    const fieldErr = rowState.errors[who] as string;
    const fieldTouched = rowState.touched[who] as string;

    return (
      <TextField
        key={`${who}-${rowState.values.id}-${formatTimeOnly(fieldValue)}`}
        size="small"
        type="text"
        placeholder="HH:mm"
        defaultValue={formatTimeOnly(fieldValue)}
        inputProps={{ maxLength: 5, inputMode: "numeric", pattern: "([01][0-9]|2[0-3]):[0-5][0-9]" }}
        onFocus={(e) => e.target.select()}
        onKeyDown={(e) => {
          // Allow control/navigation keys and combinations (copy/paste/select-all/etc.)
          if (e.ctrlKey || e.metaKey || e.altKey) return;
          if (["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "Enter", "Escape"].includes(e.key)) {
            return;
          }
          // Only allow digits and ":"
          if (!/^[0-9:]$/.test(e.key)) {
            e.preventDefault();
          }
        }}
        onChange={(e) => {
          const sanitized = e.target.value.replace(/[^0-9:]/g, "");
          if (sanitized !== e.target.value) {
            e.target.value = sanitized;
          }
        }}
        onBlur={(e) => {
          const normalized = wdpUtil.normalizeTypedTimeInput(e.target.value);
          e.target.value = normalized;
          onTimeFieldBlur(index, who, normalized);
        }}
        error={fieldTouched === "T" && fieldErr !== ""}
        helperText={fieldTouched === "T" ? fieldErr : ""}
        aria-label={isStart ? "Start time" : "Stop time"}
      />
    );
  }
  
  
  /* ################################################################################## */
  // mian tsx
  /* ################################################################################## */
  return (
    <section className="travel-section">
      {showTravelDetails && secStatus.editMode && (
        <GoabCallout type="information" emphasis="low" maxWidth="100%" heading="Before you enter travel hours" testId="tic-travel-sec-helper-callout">
          <p>Enter pre-approved operator travel hours only (accommodations/contractor home base/yard to worksite and back).</p><p>Do not include travel time from the safety briefing location to equipment  (aka Ferry time), <br/>or equipment operating time; this is recorded under Work Performed.</p><p>For double-crewed operations, refer to the Contract.</p>
        </GoabCallout>
      )}
      <div className="header-and-actions">
        <p>Are you eligible to claim operator travel expenses?</p>
        <div className="header-actions">
          {props.updateFlow   && (props.canShowEdit ?? false) && !secStatus.editMode && <GoabButton type='text' size='compact' disabled={props.isBusy ?? false} onClick={() => onEdit()}>Edit</GoabButton>}
          {props.updateFlow && secStatus.editMode && ( <>
                                    <GoabButton type='text' size='compact' onClick={() => onCancel()}>Cancel</GoabButton>
                                    <GoabButton type='secondary' size='compact' onClick={() => onSave()} disabled={!isSaveEnabled || (props.isBusy ?? false)}>Save</GoabButton>
                                    </>
                                  )
          }
        </div>
      </div>
      {secStatus.editMode && props.updateFlow ?
      <GoabRadioGroup name="Are_you_eligible" value={eligibility.local ? "1" : "2"} onChange={handleEligibility} disabled={!secStatus.editMode || !props.updateFlow}>
        <GoabRadioItem value="1" label="Yes" />
        <GoabRadioItem value="2" label="No" />
      </GoabRadioGroup>
      :<span>{eligibility.local ? "Yes" : "No"}</span>}
      {showTravelDetails && <div className="travel-table">
          <GoabTable width="100%" mb="xl">
            <thead>
              <tr><th>Location from</th><th>Location to</th><th>Start time</th><th>Stop time</th><th>Total hours</th><th></th></tr>
            </thead>
            <tbody>
              {formCtrl.map((rowState, i) => (
                <tr key={`${i}-${rowState.values.id}`}>
                  <td>
                    {secStatus.editMode?
                    <TextField size="small" value={rowState.values.from}  
                      slotProps={{ input: { 
                          inputProps: { maxLength: 128 } ,
                          endAdornment: rowState.values.from && (<GoabIconButton icon="close" variant="dark" onClick={() => onMyChange("from", "", i)}/>)
                      
                        } }}
                      onChange={(e) => onMyChange("from", e.target.value, i)}
                      onBlur={() => onMyBlur(i,"from")}
                      error={rowState.touched.from==="T" && rowState.errors.from !== ""}
                      helperText={rowState.touched.from==="T" ? rowState.errors.from : ""}
                    />:<span>{rowState.values.from}</span>
                   }
                  </td>
                  <td>
                    {secStatus.editMode? <TextField size="small" value={rowState.values.to} 
                      slotProps={{ input: { inputProps: { maxLength: 128 },endAdornment: rowState.values.to && (<GoabIconButton icon="close" variant="dark" onClick={() => onMyChange("to", "", i)}/>) } }}
                      onChange={(e) => onMyChange("to", e.target.value, i)}
                      onBlur={() => onMyBlur(i,"to")}
                      error={rowState.touched.to==="T" && rowState.errors.to !== ""}
                      helperText={rowState.touched.to==="T" ? rowState.errors.to : ""}
                    />:<span>{rowState.values.to}</span>
                    }
                  </td>
                  <td >
                    {renderTimeField(i, rowState, "startUtc")}
                  </td>
                  <td>
                    {renderTimeField(i, rowState, "finishUtc")}
                  </td>
                  <td><div className="total-hours"> {rowState.values.noOfUnits? formatHoursAsDuration(rowState.values.noOfUnits):""}</div></td>
                  <td>
                    {secStatus.editMode &&  <span className="trash-warp">
                        <GoabIconButton testId={"tic-travel-sec-th-trashBtn" + i} icon='trash' size='medium' onClick={() => onDeleteRow(i)} />
                      </span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </GoabTable>
          {secStatus.editMode && <div style={{ marginTop: "1rem" }}>
            <GoabButton type='secondary' size='compact'  onClick={() => onAddRow()}>Add new+</GoabButton>
          </div>
          }       
          
        </div>
      }
      <GoabModal heading={'Confirmation: Delete travel entries'} calloutVariant='emergency' open={deleteModalState !== null} maxWidth='30vw'
        actions={
          <GoabButtonGroup alignment='end'>
            <GoabButton testId='ud-delete-travel-entries-modal-no' type='secondary' size='compact' onClick={onCancelDeleteModal}>No, Cancel</GoabButton>
            <GoabButton testId='ud-delete-travel-entries-modal-yes' type='primary' size='compact' onClick={onConfirmDeleteModal}>Yes, delete</GoabButton>
          </GoabButtonGroup>
        }
      >
        <p>{deleteModalState?.type === 'delete-last-row'
          ? <>The row(s) associated with the deleted travel entry will be deleted from the Cost Summary table and expense eligibility will be reset to &quot;No.&quot;</>
          : <>The row(s) associated with the deleted travel entry will be deleted from the Cost Summary table.</>}
        </p>
      </GoabModal>
    </section>
  );
}